import { useState, useEffect, useRef, useMemo } from "react";
import { sqlRead, payloadOf } from "../utils/sqlClient";
import { UomSelect } from "./UomSelect";

// Search-and-pick combobox merging EstimateCostItems + EstimateCostAssemblies
// into one tagged list — same interaction pattern as CostCodePicker.jsx
// (search/browse toggle, mousedown-based picks so they fire before the
// input's blur-close, outside-click close). Built to
// replace the bare native <select> Electrical's own pickers use today
// (PanelsCircuitsPanel.jsx's Assembly column, ElecLineItemsPanel.jsx's
// Kind-then-pick form) — neither shows a price or lets you search, which is
// the specific gap this component closes. Not yet wired into either of
// those (deliberately deferred, see the Estimate feature's plan) — first
// consumer is the new cross-trade Manual Line Items panel.

const ctl = { padding: "6px 8px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: "100%", boxSizing: "border-box" };

function unitCostOf(entity) {
  return (+entity.MaterialCostPerUnit || 0) + (+entity.VendorSubCostPerUnit || 0);
}

function EntityRow({ e, onPick }) {
  return (
    <div
      onMouseDown={onPick}
      style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
      onMouseEnter={ev => ev.currentTarget.style.background = "var(--surface2)"}
      onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}
    >
      <span style={{
        fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
        background: e.kind === "assembly" ? "#eef2ff" : "var(--tint-green)",
        color: e.kind === "assembly" ? "#4338ca" : "#15803d",
      }}>{e.kind === "assembly" ? "ASM" : "ITEM"}</span>
      <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{e.code || "—"}</span>
      <span style={{ color: "var(--text2)", flex: 1 }}>{e.name}</span>
      <span style={{ fontSize: 10, color: "var(--text3)" }}>{e.uom}</span>
      <span style={{ fontSize: 11, color: "var(--text2)", minWidth: 60, textAlign: "right" }}>${unitCostOf(e.raw).toFixed(2)}</span>
    </div>
  );
}

// value: { kind: 'item'|'assembly'|null, id: number|null }
// onSelect(entity) — entity = { kind, id, code, name, uom, unitCost, raw }
export function ItemAssemblyPicker({ items, assemblies, categories = [], value, onSelect, onRequestAddNew }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const [mode, setMode]   = useState("search");
  const [kindFilter, setKindFilter] = useState("all"); // 'all' | 'item' | 'assembly'
  const [openCategories, setOpenCategories] = useState(new Set());
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const merged = useMemo(() => [
    ...items.map(i => ({ kind: "item", id: i.ItemID, code: i.Code, name: i.ItemName, uom: i.UOM, categoryId: i.CategoryID, raw: i })),
    ...assemblies.map(a => ({ kind: "assembly", id: a.AssemblyID, code: a.Code, name: a.Description, uom: a.UOM, categoryId: a.CategoryID, raw: a })),
  ], [items, assemblies]);

  const current = merged.find(e => e.kind === value?.kind && e.id === value?.id);
  const byKind  = kindFilter === "all" ? merged : merged.filter(e => e.kind === kindFilter);
  const q       = query.trim().toLowerCase();
  const filtered = (q ? byKind.filter(e => `${e.code || ""} ${e.name}`.toLowerCase().includes(q)) : byKind).slice(0, 30);

  const categoryName = id => categories.find(c => c.CategoryID === id)?.Name || "Uncategorized";
  const byCategory = useMemo(() => {
    const map = new Map();
    for (const e of byKind) {
      const key = e.categoryId ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return [...map.entries()];
  }, [byKind]);

  function toggleCategory(key) {
    setOpenCategories(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function pick(e) {
    onSelect(e);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        value={open && mode === "search" ? query : (current ? `${current.code ? current.code + " — " : ""}${current.name}` : "")}
        readOnly={mode === "browse"}
        onFocus={() => { setOpen(true); if (mode === "search") setQuery(""); }}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search item or assembly…"
        style={{ ...ctl, fontSize: 12 }}
      />
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, minWidth: 380, zIndex: 50,
          background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 320, overflowY: "auto",
        }}>
          <div style={{ display: "flex", gap: 4, padding: 6, borderBottom: "1px solid var(--cs-border)", position: "sticky", top: 0, background: "var(--surface)" }}>
            {["search", "browse"].map(m => (
              <button
                key={m}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setMode(m)}
                style={{
                  flex: 1, padding: "4px 8px", fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                  border: "1px solid var(--cs-border)", borderRadius: 5, cursor: "pointer",
                  background: mode === m ? "var(--cs-blue)" : "var(--surface)",
                  color: mode === m ? "#fff" : "var(--text2)",
                }}
              >{m}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4, padding: "4px 6px", borderBottom: "1px solid var(--cs-border)" }}>
            {[["all", "All"], ["item", "Items"], ["assembly", "Assemblies"]].map(([k, label]) => (
              <button
                key={k}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setKindFilter(k)}
                style={{
                  flex: 1, padding: "3px 6px", fontSize: 10.5, fontWeight: 600,
                  border: "1px solid var(--cs-border)", borderRadius: 5, cursor: "pointer",
                  background: kindFilter === k ? "#eef2ff" : "var(--surface)",
                  color: kindFilter === k ? "#4338ca" : "var(--text3)",
                }}
              >{label}</button>
            ))}
          </div>

          {mode === "search" && (
            <>
              {filtered.length === 0 && <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--text3)" }}>No matches</div>}
              {filtered.map(e => <EntityRow key={`${e.kind}-${e.id}`} e={e} onPick={() => pick(e)} />)}
            </>
          )}

          {mode === "browse" && byCategory.map(([catId, entities]) => {
            const catOpen = openCategories.has(catId);
            return (
              <div key={catId ?? "none"}>
                <div
                  onMouseDown={e => { e.preventDefault(); toggleCategory(catId); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", cursor: "pointer",
                    fontSize: 11.5, fontWeight: 700, color: "var(--text2)", background: "var(--surface2)",
                    borderTop: "1px solid var(--cs-border)",
                  }}
                >
                  <span style={{ display: "inline-block", transform: catOpen ? "rotate(90deg)" : "none", transition: "transform 0.12s", fontSize: 9, color: "var(--text3)" }}>▸</span>
                  {categoryName(catId)}
                  <span style={{ marginLeft: "auto", fontWeight: 400, color: "var(--text3)" }}>{entities.length}</span>
                </div>
                {catOpen && entities.map(e => <EntityRow key={`${e.kind}-${e.id}`} e={e} onPick={() => pick(e)} />)}
              </div>
            );
          })}

          {onRequestAddNew && (
            <div
              onMouseDown={e => { e.preventDefault(); setOpen(false); onRequestAddNew(); }}
              style={{
                padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "var(--cs-blue)", cursor: "pointer",
                borderTop: "1px solid var(--cs-border)", position: "sticky", bottom: 0, background: "var(--surface)",
              }}
            >+ Add new Item/Assembly…</div>
          )}
        </div>
      )}
    </div>
  );
}

const flyoutLabel = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };
const flyoutInput = { height: 30, width: "100%", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12.5, color: "var(--text)", background: "var(--surface)", outline: "none", boxSizing: "border-box" };

// Inline "can't find it, add it" companion — same fixed-right-panel shape as
// CostCodePicker.jsx's AddCostCodeFlyout. Calls the same sp_SaveEstimateCost
// Item/sp_SaveEstimateCostAssembly procs EstimateCostLibrary.jsx already uses
// for full CRUD; this is deliberately a minimal subset of those fields — a
// quick add mid-workflow, not a replacement for the full Estimate Library
// admin page for anything beyond the basics.
export function AddItemOrAssemblyFlyout({ categories = [], onClose, onSaved }) {
  const [kind, setKind] = useState("assembly");
  const [form, setForm] = useState({ code: "", name: "", categoryId: "", uom: "lf", materialCostPerUnit: "", laborHrPerUnit: "", vendorSubCostPerUnit: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Admin-managed unit catalog (db/81). Read-only here — estimators pick from
  // it; adding a unit is the Estimate Engine ▸ Units of Measure tab's job.
  // A failed load leaves the picker on the built-in fallback list.
  const [uoms, setUoms] = useState([]);
  useEffect(() => {
    let alive = true;
    sqlRead("EXEC sp_GetEstimateUnitsOfMeasure").then(rows => { if (alive) setUoms(rows || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.name.trim()) { setError(kind === "assembly" ? "Description is required." : "Item name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const proc = kind === "assembly" ? "sp_SaveEstimateCostAssembly" : "sp_SaveEstimateCostItem";
      const payload = kind === "assembly"
        ? {
            code: form.code.trim() || null, category_id: form.categoryId ? +form.categoryId : null,
            description: form.name.trim(), uom: form.uom,
            material_cost_per_unit: form.materialCostPerUnit === "" ? 0 : +form.materialCostPerUnit,
            labor_hr_per_unit: form.laborHrPerUnit === "" ? 0 : +form.laborHrPerUnit,
            vendor_sub_cost_per_unit: form.vendorSubCostPerUnit === "" ? 0 : +form.vendorSubCostPerUnit,
          }
        : {
            code: form.code.trim() || null, category_id: form.categoryId ? +form.categoryId : null,
            item_name: form.name.trim(), uom: form.uom,
            material_cost_per_unit: form.materialCostPerUnit === "" ? null : +form.materialCostPerUnit,
            labor_hr_per_unit: form.laborHrPerUnit === "" ? null : +form.laborHrPerUnit,
            vendor_sub_cost_per_unit: form.vendorSubCostPerUnit === "" ? 0 : +form.vendorSubCostPerUnit,
          };
      const result = await sqlRead(`EXEC ${proc} @payload='${payloadOf(payload)}'`);
      const row = result?.[0];
      const id = kind === "assembly" ? row?.AssemblyID : row?.ItemID;
      if (!id) { setError("Save failed — no ID returned."); return; }
      // Shaped to match the PascalCase columns sp_GetEstimateCostLibrary's
      // SELECT * returns, so this optimistic row renders identically to the
      // real one once the next library reload replaces it.
      const raw = kind === "assembly"
        ? {
            AssemblyID: id, Code: payload.code, CategoryID: payload.category_id,
            Description: payload.description, UOM: payload.uom,
            MaterialCostPerUnit: payload.material_cost_per_unit, LaborHrPerUnit: payload.labor_hr_per_unit,
            VendorSubCostPerUnit: payload.vendor_sub_cost_per_unit, DefaultLaborFactorID: null, IsActive: true,
          }
        : {
            ItemID: id, Code: payload.code, CategoryID: payload.category_id,
            ItemName: payload.item_name, UOM: payload.uom,
            MaterialCostPerUnit: payload.material_cost_per_unit, LaborHrPerUnit: payload.labor_hr_per_unit,
            VendorSubCostPerUnit: payload.vendor_sub_cost_per_unit, IsActive: true,
          };
      onSaved({
        kind, id,
        code: payload.code, name: kind === "assembly" ? payload.description : payload.item_name,
        uom: payload.uom, categoryId: payload.category_id, raw,
      });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, width: 380, height: "100vh",
      background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
      display: "flex", flexDirection: "column",
      boxShadow: "-4px 0 24px rgba(0,0,0,0.07)", zIndex: 400,
    }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cs-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Add {kind === "assembly" ? "Assembly" : "Item"}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)", padding: "2px 6px" }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["assembly", "item"].map(k => (
            <button key={k} onClick={() => setKind(k)} style={{
              flex: 1, height: 30, fontSize: 12, fontWeight: 600, textTransform: "capitalize", cursor: "pointer",
              border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
              background: kind === k ? "var(--cs-blue)" : "#fff", color: kind === k ? "#fff" : "var(--text2)",
            }}>{k}</button>
          ))}
        </div>
        <div>
          <label style={flyoutLabel}>Code</label>
          <input style={{ ...flyoutInput, fontFamily: "monospace" }} value={form.code} onChange={e => set("code", e.target.value)} />
        </div>
        <div>
          <label style={flyoutLabel}>{kind === "assembly" ? "Description *" : "Item Name *"}</label>
          <input style={flyoutInput} value={form.name} onChange={e => set("name", e.target.value)} />
        </div>
        <div>
          <label style={flyoutLabel}>Category</label>
          <select style={flyoutInput} value={form.categoryId} onChange={e => set("categoryId", e.target.value)}>
            <option value="">—</option>
            {categories.map(c => <option key={c.CategoryID} value={c.CategoryID}>{c.Name}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={flyoutLabel}>UOM</label>
            <UomSelect style={flyoutInput} value={form.uom} onChange={v => set("uom", v)} uoms={uoms} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={flyoutLabel}>Material $/unit</label>
            <input type="number" step="0.01" style={flyoutInput} value={form.materialCostPerUnit} onChange={e => set("materialCostPerUnit", e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={flyoutLabel}>Labor hr/unit</label>
            <input type="number" step="0.001" style={flyoutInput} value={form.laborHrPerUnit} onChange={e => set("laborHrPerUnit", e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={flyoutLabel}>Vendor/sub $/unit</label>
            <input type="number" step="0.01" style={flyoutInput} value={form.vendorSubCostPerUnit} onChange={e => set("vendorSubCostPerUnit", e.target.value)} />
          </div>
        </div>
        <p style={{ fontSize: 11, color: "var(--text3)", margin: 0 }}>
          This is a quick add — for labor factors, quantity formulas, or a Takeoff Mapping, use the full Estimate Library (Electrical → Assembly Library).
        </p>
      </div>

      <div style={{ padding: "12px 18px", borderTop: "1px solid var(--cs-border)", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        {error && (
          <div style={{ fontSize: 12, color: "var(--red, #dc2626)", padding: "6px 10px", background: "var(--tint-red)", borderRadius: "var(--radius)" }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, height: 34, fontSize: 13, fontWeight: 500,
            border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
            background: "none", color: "var(--text2)", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, height: 34, fontSize: 13, fontWeight: 600,
            border: "none", borderRadius: "var(--radius)",
            background: saving ? "var(--cs-border)" : "var(--cs-blue)",
            color: "#fff", cursor: saving ? "default" : "pointer",
          }}>{saving ? "Saving…" : `Add ${kind === "assembly" ? "Assembly" : "Item"}`}</button>
        </div>
      </div>
    </div>
  );
}
